import gettext from '@sdk/lang/gettext';

export const FEATURES_INFO = {
  conversationRestrictions: {
    customBenefitsText: gettext('Benefits of using this setting:'),
    header: gettext('Customize who is allowed to post to a group conversation'),
    descriptions: [
      gettext('Keep the content within a conversation appropriate and focused on a topic'),
      gettext('Ensure that important messages will not be lost among multiple posts'),
    ],
  },
  tags: {
    name: gettext('tags'),
    header: gettext('Add specifics to your shifts'),
    descriptions: [
      gettext('Provide further details to shifts, rosters and reports'),
      gettext('Ensure proper staffing for all sites, jobs and zones'),
      gettext('Keep employees clear on what is expected of them'),
    ],
  },
  paidTimeOffs: {
    name: gettext('PTO'),
    header: gettext('Make your leave management easier'),
    descriptions: [
      gettext('Keep track of sick days, vacation days, and personal days'),
      gettext('View PTO right on the schedule to avoid schedule conflicts'),
      gettext('Track PTO in reports and make payroll processing faster'),
    ],
  },
  reports: {
    name: gettext('reports'),
    header: gettext('Stay on top of your data'),
    descriptions: [
      gettext('Understand your labor and sales information better'),
      gettext('Audit attendance to evaluate employee performance'),
      gettext('Use insights to adjust, and drive your business forward'),
      gettext('Keep a record of your schedule and time tracking data'),
    ],
  },
  shiftTasks: {
    name: gettext('tasks'),
    header: gettext('Attach tasks to shifts and keep your employees accountable'),
    descriptions: [
      gettext('Add clarity to what needs to be done during each shift'),
      gettext('Save time by reusing task templates for recurring work'),
      gettext('Keep employees clear and aligned on what to do next'),
      gettext('Keep employees accountable and follow their progress'),
    ],
  },
  messages: {
    name: gettext('messages'),
    header: gettext('Communicate from a single platform'),
    descriptions: [
      gettext('Make internal communication simple and keep it in one place'),
      gettext('Create a collaborative environment no matter where you are'),
      gettext('Keep your team engaged and your communication organized'),
      gettext('Create a sense of unity among every person on your team'),
    ],
  },
  // We don't want to let users know that they will get private messages for free during trial
  // so groupMessages and messages definition are identical at the time
  groupMessages: {
    name: gettext('messages'),
    header: gettext('Communicate from a single platform'),
    descriptions: [
      gettext('Streamline and simplify internal communications'),
      gettext('Foster collaboration from anywhere'),
      gettext('Keep your team engaged and connected'),
    ],
  },
  unavailability: {
    name: gettext('unavailability'),
    header: gettext('Keep track of unavailability'),
    customBenefitsText: gettext('Benefits of specifying [[featureName]] on Sling'),
    descriptions: [
      gettext('Avoid schedule conflicts by seeing unavailability on the schedule'),
      gettext('Understand availability of your team and ensure proper staffing'),
      gettext('Find last minute shift covers for changing schedules without having to call anyone'),
    ],
  },
  laborCost: {
    name: gettext('labor management'),
    header: gettext('Optimize the cost of labor as you build the schedule'),
    descriptions: [
      gettext('Forecast weekly labor cost and estimate labor %'),
      gettext('Keep labor cost within budget and avoid overstaffing'),
      gettext('Analyze your labor data and optimize your spending'),
      gettext('Avoid costly overtime as you build your schedule'),
    ],
  },
  iCalendarSync: {
    name: gettext('calendar sync'),
    header: gettext('Sync your work schedule to any calendar app'),
    descriptions: [
      gettext('Overlay your work schedule over your personal events'),
      gettext('Balance your professional and personal commitments better'),
      gettext('Keep your full schedule in one place and never miss a beat'),
    ],
  },
  userDocuments: {
    name: gettext('keeping documents'),
    header: gettext('Keep track of employee documents'),
    descriptions: [
      gettext('Track employee licenses and certifications'),
      gettext('Get reminded of expiration dates and renewals'),
      gettext('Keep documents in one place and ditch paper files'),
    ],
  },
  laborCompliance: {
    name: gettext('labor compliance'),
    header: gettext('Keep your schedules compliant'),
    descriptions: [
      gettext('Avoid clopening shifts when scheduling'),
      gettext('Consider rest days between work days'),
      gettext('Ensure employees are properly rested'),
    ],
  },
  projectedSalesSync: {
    name: gettext('projected sales sync'),
    header: gettext('Sync projected sales from Toast'),
    descriptions: [
      gettext('Leverage forecasts powered by Toast'),
      gettext('Save time with automated projection'),
      gettext('Optimize schedules and control your labor cost'),
    ],
  },
  breaksAttestation: {
    name: gettext('attestation'),
    header: gettext('Stay compliant with your regional labor requirements'),
    descriptions: [
      gettext('Confirm whether employees took their required breaks'),
      gettext('Compensate employees for missed breaks'),
      gettext('Keep a record of meal and rest breaks for the future'),
    ],
  },
  automatedBreaks: {
    name: gettext('automated breaks'),
    header: gettext('Stay compliant with your regional labor requirements'),
    descriptions: [
      gettext('Ensure employees are able to take required breaks'),
      gettext('Provide employees with the necessary rest time'),
      gettext('Stay compliant while avoiding employee burnout'),
    ],
  },
  shiftFeedback: {
    name: gettext('shift feedback'),
    header: gettext('Get feedback from your employees'),
    descriptions: [
      gettext('Gain insight into employee shift satisfaction'),
      gettext('Compare shift feedback across locations, positions and more'),
      gettext('Get ahead of potential problems to support retention'),
    ],
  },
  bulkMessages: {
    name: gettext('bulk messages'),
    customBenefitsText: gettext('Benefits of using this setting:'),
    header: gettext('Reach multiple people privately at the click of a button'),
    descriptions: [
      gettext('Save time sharing the same message with multiple people privately'),
      gettext('Protect others from receiving unnecessary replies, and eliminate noise'),
    ],
  },
  freeUserLimit: {
    name: gettext('free user limit'),
    header: gettext("You've reached your user limit. Upgrade to add more users."),
    customBenefitsText: gettext('Benefits of upgrading to Sling Premium:'),
    descriptions: [
      gettext('Add as many team members as you need with no limits'),
      gettext('Gain access to new features and enhance your team productivity and collaboration'),
    ],
  },
};

export const ACCESS_LEVEL_NAMES = {
  0: gettext('Free'),
  1: gettext('Premium'),
  2: gettext('Business'),
  3: gettext('Business'),
};
